import { PdfFont, PdfSize, PageGen } from '../pdf';

export interface TextItem {
  type: 'box' | 'glue' | 'penalty';
  width: number;
  stretch?: number;
  shrink?: number;
  penalty?: number;
  flagged?: boolean;
  content?: string;
}

export interface BreakPoint {
  position: number;
  line: number;
  fitness: number;
  totalDemerits: number;
  previous: BreakPoint | null;
  ratio: number;
}

export interface LineBreakResult {
  breaks: BreakPoint[];
  items: TextItem[];
}

export interface LayoutParams {
  font: PdfFont;
  page?: PageGen;
  lineWidth: number;
  tolerance: number;
}

const INFINITE_PENALTY = 1000000;
const DEMERIT_PENALTY = 1;
const DEMERIT_FLAGGED = 100;
const DEMERIT_FITNESS = 10;

export function textToItems(text: string, font: PdfFont, page?: PageGen): TextItem[] {
  const items: TextItem[] = [];
  const words = text.split(/(\s+)/);

  const spaceWidth = page
    ? page.pdf.getFont(font.font).widthOfTextAtSize(' ', font.size)
    : font.size * 0.4;

  const spaceStretch = spaceWidth * 0.5;
  const spaceShrink = spaceWidth * 0.3;

  for (let i = 0; i < words.length; i++) {
    const word = words[i];

    if (word.length === 0) continue;

    if (/^\s+$/.test(word)) {
      items.push({
        type: 'glue',
        width: spaceWidth,
        stretch: spaceStretch,
        shrink: spaceShrink,
      });
    } else {
      const wordWidth = page
        ? page.pdf.getFont(font.font).widthOfTextAtSize(word, font.size)
        : word.length * font.size * 0.6;

      items.push({
        type: 'box',
        width: wordWidth,
        content: word,
      });

      if (i < words.length - 1) {
        items.push({
          type: 'glue',
          width: spaceWidth,
          stretch: spaceStretch,
          shrink: spaceShrink,
        });
      }
    }
  }

  items.push({
    type: 'penalty',
    width: 0,
    penalty: INFINITE_PENALTY,
    flagged: false,
  });

  return items;
}

export function breakLines(items: TextItem[], lineWidth: number): BreakPoint[] {
  const breaks: BreakPoint[] = [];

  const n = items.length;
  const dp: BreakPoint[] = new Array(n + 1);

  dp[0] = {
    position: 0,
    line: 0,
    fitness: 0,
    totalDemerits: 0,
    previous: null,
    ratio: 0,
  };

  for (let i = 1; i <= n; i++) {
    dp[i] = {
      position: i,
      line: 0,
      fitness: 3,
      totalDemerits: INFINITE_PENALTY,
      previous: null,
      ratio: 0,
    };
  }

  for (let i = 0; i < n; i++) {
    let curX = 0;
    let curStretch = 0;
    let curShrink = 0;
    let active = false;

    for (let j = i + 1; j <= n; j++) {
      const item = items[j - 1];

      if (item.type === 'box') {
        curX += item.width;
        active = true;
      } else if (item.type === 'glue') {
        if (active) {
          curX += item.width;
          curStretch += item.stretch || 0;
          curShrink += item.shrink || 0;
        }
      } else if (item.type === 'penalty') {
        if (!active) continue;

        const actual = curX;
        const stretch = curStretch;
        const shrink = curShrink;

        let ratio = 0;

        if (actual < lineWidth) {
          if (stretch > 0) {
            ratio = (lineWidth - actual) / stretch;
          } else {
            ratio = 1;
          }
        } else if (actual > lineWidth) {
          if (shrink > 0) {
            ratio = (lineWidth - actual) / shrink;
          } else {
            ratio = -1;
          }
        } else {
          ratio = 0;
        }

        if (ratio < -1) continue;

        const fitness = ratio < -0.5 ? 0 : ratio < 0.5 ? 1 : ratio < 1 ? 2 : 3;

        const badness = Math.abs(ratio);
        const demerits = (DEMERIT_PENALTY + badness * badness * badness)**2;

        let additionalDemerits = 0;
        if (item.flagged) {
          additionalDemerits += DEMERIT_FLAGGED;
        }

        const line = (dp[i].line || 0) + 1;
        const prevFitness = dp[i].fitness;

        if (Math.abs(fitness - prevFitness) > 1) {
          additionalDemerits += DEMERIT_FITNESS;
        }

        const totalDemerits = dp[i].totalDemerits + demerits + additionalDemerits;

        if (totalDemerits < dp[j].totalDemerits) {
          dp[j] = {
            position: j,
            line,
            fitness,
            totalDemerits,
            previous: dp[i],
            ratio,
          };
        }

        if (item.penalty === INFINITE_PENALTY) break;

        active = false;
        curX = 0;
        curStretch = 0;
        curShrink = 0;
      }
    }
  }

  let cur = dp[n];
  while (cur && cur.previous) {
    breaks.unshift(cur);
    cur = cur.previous;
  }

  return breaks;
}

export function layoutParagraph(text: string, params: LayoutParams): LineBreakResult {
  const items = textToItems(text, params.font, params.page);
  const breaks = breakLines(items, params.lineWidth);

  return { breaks, items };
}

export function measureText(text: string, font: PdfFont, page?: PageGen): PdfSize {
  const items = textToItems(text, font, page);

  let totalWidth = 0;
  for (const item of items) {
    if (item.type === 'box') {
      totalWidth += item.width;
    }
  }

  return new PdfSize(totalWidth, font.size * 1.2);
}
